/* eslint-disable react/no-unknown-property */

import { Suspense, useRef, useMemo } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Float, MeshDistortMaterial, Text3D, Center, RoundedBox } from "@react-three/drei";
import * as THREE from "three";

// Shared metallic pink -> violet gradient texture
function useGradientTexture() {
  return useMemo(() => {
    const c = document.createElement("canvas");
    c.width = 512;
    c.height = 512;
    const ctx = c.getContext("2d");
    const g = ctx.createLinearGradient(0, 0, 512, 512);
    g.addColorStop(0.0, "#F4A9E8");
    g.addColorStop(0.35, "#D19BF7");
    g.addColorStop(0.65, "#9E7BF5");
    g.addColorStop(1.0, "#5B34DE");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 512, 512);
    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
  }, []);
}

function useSpin(group, scrollRef, opts = {}) {
  const { baseY = -0.35, baseX = -0.15, ySpin = 1.4 } = opts;
  useFrame((state) => {
    const t = state.clock.getElapsedTime();
    const scroll = scrollRef?.current ?? 0;
    const px = state.pointer.x;
    const py = state.pointer.y;
    if (group.current) {
      group.current.rotation.y = THREE.MathUtils.lerp(
        group.current.rotation.y,
        baseY + px * 0.5 + Math.sin(t * 0.3) * 0.25 + scroll * Math.PI * ySpin,
        0.06
      );
      group.current.rotation.x = THREE.MathUtils.lerp(
        group.current.rotation.x,
        baseX - py * 0.3 + Math.sin(t * 0.45) * 0.1 + scroll * 0.5,
        0.06
      );
      group.current.position.y = Math.sin(t * 0.6) * 0.1;
    }
  });
}

/* ---------- Concept: interlocking loops (default) ---------- */
function LoopsMark({ scrollRef }) {
  const group = useRef();
  const gradient = useGradientTexture();
  useSpin(group, scrollRef);
  const R = 0.78;
  const tube = 0.24;
  const loops = [
    [-0.85, 0, -0.06],
    [0.05, 0, 0.12],
    [0.95, 0, -0.06],
  ];
  return (
    <group ref={group} scale={0.85} position={[2.0, 0.1, 0]}>
      {loops.map((pos, i) => (
        <mesh key={i} position={pos}>
          <torusGeometry args={[R, tube, 40, 120]} />
          <meshStandardMaterial
            map={gradient}
            emissiveMap={gradient}
            emissive={"#ffffff"}
            emissiveIntensity={0.14}
            metalness={0.35}
            roughness={0.22}
          />
        </mesh>
      ))}
    </group>
  );
}

/* ---------- Concept B: liquid metal blob ---------- */
function BlobMark({ scrollRef }) {
  const group = useRef();
  const gradient = useGradientTexture();
  useSpin(group, scrollRef, { baseY: 0, ySpin: 0.8 });
  return (
    <group ref={group} scale={1.35} position={[2.0, 0.1, 0]}>
      <mesh>
        <icosahedronGeometry args={[1, 24]} />
        <MeshDistortMaterial
          map={gradient}
          emissiveMap={gradient}
          emissive={"#ffffff"}
          emissiveIntensity={0.12}
          metalness={0.55}
          roughness={0.15}
          distort={0.42}
          speed={1.8}
        />
      </mesh>
    </group>
  );
}

/* ---------- Concept C: extruded CMD+ type ---------- */
function TypeMark({ scrollRef }) {
  const group = useRef();
  const gradient = useGradientTexture();
  useSpin(group, scrollRef, { baseY: -0.2, ySpin: 0.7 });
  return (
    <group ref={group} scale={1.0} position={[1.7, 0.1, 0]}>
      <Center>
        <Text3D
          font="/fonts/helvetiker_bold.typeface.json"
          size={1.05}
          height={0.45}
          curveSegments={12}
          bevelEnabled
          bevelThickness={0.05}
          bevelSize={0.03}
          bevelSegments={5}
        >
          CMD+
          <meshStandardMaterial
            map={gradient}
            emissiveMap={gradient}
            emissive={"#ffffff"}
            emissiveIntensity={0.12}
            metalness={0.5}
            roughness={0.2}
          />
        </Text3D>
      </Center>
    </group>
  );
}

/* ---------- Concept D: floating ripple targets ---------- */
// Smooth equirectangular gradient used as an environment reflection so the
// metal blends continuously (no hard UV seam).
function useEnvTexture() {
  return useMemo(() => {
    const c = document.createElement("canvas");
    c.width = 512;
    c.height = 256;
    const ctx = c.getContext("2d");
    const g = ctx.createLinearGradient(0, 0, 0, 256);
    g.addColorStop(0.0, "#FBD3F2");
    g.addColorStop(0.32, "#C79BF7");
    g.addColorStop(0.62, "#7B4BE6");
    g.addColorStop(0.85, "#3E1FA6");
    g.addColorStop(1.0, "#1C0B4D");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 512, 256);
    // soft top-left highlight for a metallic sheen
    const hl = ctx.createRadialGradient(150, 60, 8, 150, 60, 150);
    hl.addColorStop(0, "rgba(255,255,255,0.9)");
    hl.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = hl;
    ctx.fillRect(0, 0, 512, 256);
    const tex = new THREE.CanvasTexture(c);
    tex.mapping = THREE.EquirectangularReflectionMapping;
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
  }, []);
}

function Target({ scale = 1, position, env }) {
  return (
    <group position={position} scale={scale}>
      {/* outer ring */}
      <mesh position={[0, 0, 0]}>
        <torusGeometry args={[0.9, 0.16, 48, 140]} />
        <meshStandardMaterial
          color={"#8149D8"}
          metalness={1.0}
          roughness={0.2}
          envMap={env}
          envMapIntensity={1.6}
        />
      </mesh>
      {/* middle ring */}
      <mesh position={[0, 0, 0.08]}>
        <torusGeometry args={[0.52, 0.14, 48, 140]} />
        <meshStandardMaterial
          color={"#8149D8"}
          metalness={1.0}
          roughness={0.2}
          envMap={env}
          envMapIntensity={1.6}
        />
      </mesh>
      {/* center dome */}
      <mesh position={[0, 0, 0.16]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.26, 0.26, 0.18, 48]} />
        <meshStandardMaterial
          color={"#3A1E86"}
          metalness={0.95}
          roughness={0.34}
          envMap={env}
          envMapIntensity={0.9}
        />
      </mesh>
    </group>
  );
}

function TargetsMark({ scrollRef }) {
  const group = useRef();
  const env = useEnvTexture();
  useSpin(group, scrollRef, { baseY: 0, baseX: -0.05, ySpin: 0.4 });
  const targets = [
    { position: [-0.3, 0.1, 0], scale: 1.15 },
    { position: [1.7, 1.15, 0.2], scale: 0.6 },
    { position: [1.95, -0.55, 0.1], scale: 0.46 },
    { position: [1.0, -1.45, 0.15], scale: 0.36 },
  ];
  return (
    <group ref={group} scale={0.82} position={[1.9, 0.1, 0]}>
      {targets.map((t, i) => (
        <Target key={i} {...t} env={env} />
      ))}
    </group>
  );
}

/* ---------- Concept E: 4-point sparkle star ---------- */
function StarMark({ scrollRef }) {
  const group = useRef();
  const gradient = useGradientTexture();
  useSpin(group, scrollRef, { baseY: -0.15, baseX: -0.1, ySpin: 0.7 });

  const geometry = useMemo(() => {
    const o = 1.15; // tip length
    const k = 0.16; // concavity control (near center)
    const shape = new THREE.Shape();
    shape.moveTo(0, o);
    shape.quadraticCurveTo(k, k, o, 0);
    shape.quadraticCurveTo(k, -k, 0, -o);
    shape.quadraticCurveTo(-k, -k, -o, 0);
    shape.quadraticCurveTo(-k, k, 0, o);
    const geo = new THREE.ExtrudeGeometry(shape, {
      depth: 0.28,
      bevelEnabled: true,
      bevelThickness: 0.06,
      bevelSize: 0.05,
      bevelSegments: 6,
      curveSegments: 64,
    });
    geo.center();
    return geo;
  }, []);

  return (
    <group ref={group} scale={1.15} position={[2.0, 0.1, 0]}>
      <mesh geometry={geometry}>
        <meshStandardMaterial
          map={gradient}
          emissiveMap={gradient}
          emissive={"#ffffff"}
          emissiveIntensity={0.16}
          metalness={0.4}
          roughness={0.18}
        />
      </mesh>
    </group>
  );
}

/* ---------- Concept: torus knot ---------- */
function KnotMark({ scrollRef }) {
  const group = useRef();
  const env = useEnvTexture();
  useSpin(group, scrollRef, { baseY: 0, baseX: -0.1, ySpin: 1.2 });
  return (
    <group ref={group} scale={1.15} position={[2.0, 0.1, 0]}>
      <mesh>
        <torusKnotGeometry args={[0.8, 0.26, 260, 36, 2, 3]} />
        <meshStandardMaterial
          color={"#8149D8"}
          metalness={1.0}
          roughness={0.2}
          envMap={env}
          envMapIntensity={1.6}
        />
      </mesh>
    </group>
  );
}

/* ---------- Concept: faceted gem ---------- */
function GemMark({ scrollRef }) {
  const group = useRef();
  const env = useEnvTexture();
  useSpin(group, scrollRef, { baseY: 0, baseX: -0.12, ySpin: 1.0 });
  return (
    <group ref={group} scale={1.5} position={[2.0, 0.1, 0]}>
      <mesh>
        <icosahedronGeometry args={[1, 0]} />
        <meshStandardMaterial
          color={"#8149D8"}
          metalness={1.0}
          roughness={0.14}
          flatShading
          envMap={env}
          envMapIntensity={1.7}
        />
      </mesh>
    </group>
  );
}

/* ---------- Concept: soft rounded cube ---------- */
function CubeMark({ scrollRef }) {
  const group = useRef();
  const env = useEnvTexture();
  useSpin(group, scrollRef, { baseY: -0.4, baseX: -0.3, ySpin: 1.0 });
  return (
    <group ref={group} scale={1.35} position={[2.0, 0.1, 0]}>
      <RoundedBox args={[1.4, 1.4, 1.4]} radius={0.28} smoothness={8}>
        <meshStandardMaterial
          color={"#8149D8"}
          metalness={1.0}
          roughness={0.18}
          envMap={env}
          envMapIntensity={1.6}
        />
      </RoundedBox>
    </group>
  );
}

const CONCEPTS = {
  loops: LoopsMark,
  blob: BlobMark,
  type: TypeMark,
  targets: TargetsMark,
  star: StarMark,
  knot: KnotMark,
  gem: GemMark,
  cube: CubeMark,
};

export default function Scene3D({ scrollRef, variant = "knot" }) {
  const Mark = CONCEPTS[variant] || LoopsMark;
  return (
    <Canvas
      dpr={[1, 1.8]}
      camera={{ position: [0, 0, 6], fov: 42 }}
      gl={{ alpha: true, antialias: true }}
      style={{ background: "transparent" }}
    >
      <ambientLight intensity={0.9} />
      <spotLight
        position={[5, 6, 5]}
        angle={0.5}
        penumbra={1}
        intensity={3.4}
        color={"#ffffff"}
      />
      <pointLight position={[-6, -2, 2]} intensity={2.2} color={"#F4A9E8"} />
      <pointLight position={[4, -4, 4]} intensity={1.8} color={"#8A2BE2"} />
      <Suspense fallback={null}>
        <Float speed={1.3} rotationIntensity={0.3} floatIntensity={0.5}>
          <Mark scrollRef={scrollRef} />
        </Float>
      </Suspense>
    </Canvas>
  );
}
